import React, { useState } from "react";
import { Form, Button, Row, Col, Alert, Container } from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import { API, API_KEY_HEADERS } from "../config/apiConfig";
import SuccessModal from "../components/SuccessModal";

// Validation schema using Yup
const SubmissionSchema = Yup.object().shape({
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  email: Yup.string()
    .email("Please enter a valid email address")
    .required("Email is required"),
  affiliation: Yup.string().required("Institution is required"),
  title: Yup.string().required("Article title is required"),
  abstract: Yup.string().required("Abstract is required"),
  keywords: Yup.string().required("Keywords are required"),
  agreement: Yup.boolean().oneOf([true], "You must agree to the terms"),
});

const SubmissionForm = () => {
  const navigate = useNavigate();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (file) {
      // Simple file validation
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      const maxSize = 10 * 1024 * 1024; // 10MB

      if (!allowedTypes.includes(file.type)) {
        toast.error(
          "Invalid file type. Only PDF, DOC, and DOCX files are allowed.",
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
        event.target.value = "";
        return;
      }

      if (file.size > maxSize) {
        toast.error(
          `File size exceeds 10MB limit. Current size: ${(
            file.size /
            1024 /
            1024
          ).toFixed(2)} MB`,
          {
            position: "top-right",
            autoClose: 7000,
          }
        );
        event.target.value = "";
        return;
      }

      setSelectedFile(file);

      // Show success toast for file selection
      toast.success(`File selected: ${file.name}`, {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    

    // Check if file is selected
    if (!selectedFile) {
      toast.error("Please select a file to upload", {
        position: "top-right",
        autoClose: 5000,
      });
      return;
    }

    setIsSubmitting(true);

    try {

      const uploadUrlResponse = await axios.post(
        API.getUploadUrl,
        {
          fileName: selectedFile.name,
          fileType: selectedFile.type,
          fileSize: selectedFile.size,
        },
        {
          headers: API_KEY_HEADERS,
        }
      );
      const { uploadUrl, fileKey } = uploadUrlResponse.data;
      await axios.put(uploadUrl, selectedFile, {
        headers: { "Content-Type": selectedFile.type },
      });

      const articleData = {
        ...values,
        fileName: selectedFile.name,
        fileType: selectedFile.type,
        fileSize: selectedFile.size,
        fileKey: fileKey, // Include fileKey in the submission
      };

      const response = await axios.post(API.submitArticle, articleData, {
        headers: API_KEY_HEADERS,
      });

      // setShowSuccessModal(true);

      // Show success toast
      toast.success(
        "Article submitted successfully! We'll review it and get back to you soon.",
        {
          position: "top-right",
          autoClose: 5000,
        }
      );


      setTimeout(() => {
        navigate("/");
      }, 1000);
      

      // Reset form
      resetForm();
      setSelectedFile(null);
    } catch (error) {

      let errorMessage = "Submission failed. ";
      if (error.response?.data?.error) {
        errorMessage += error.response.data.error;
      } else if (error.message) {
        errorMessage += error.message;
      } else {
        errorMessage += "Unknown error occurred";
      }

      // Show error toast
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 7000,
      });
    } finally {
      setIsSubmitting(false);
      setSubmitting(false);
    }
  };

  const closeSuccessModal = () => {
    setShowSuccessModal(false);
    setTimeout(() => {
      navigate("/");
    }, 1000);
  };

  return (
    <>
      <div
        style={{
          paddingTop: "90px",
        }}
      >
        <Container>
          <h1 className="mb-4">Research Article Submission</h1>

          <div className="card shadow-sm p-4">
            <Formik
              initialValues={{
                firstName: "",
                lastName: "",
                email: "",
                affiliation: "",
                title: "",
                abstract: "",
                keywords: "",
                agreement: false,
              }}
              validationSchema={SubmissionSchema}
              onSubmit={handleSubmit}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
              }) => (
                <Form onSubmit={handleSubmit}>
                  <h4 className="mb-4">Author Information</h4>

                  <Row className="mb-3">
                    <Col md="6">
                      <Form.Group className="mb-3">
                        <Form.Label>First Name *</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter first name"
                          name="firstName"
                          value={values.firstName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.firstName && errors.firstName}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.firstName}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col md="6">
                      <Form.Group className="mb-3">
                        <Form.Label>Last Name *</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter last name"
                          name="lastName"
                          value={values.lastName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.lastName && errors.lastName}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.lastName}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Email address *</Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="Enter email"
                      name="email"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.email && errors.email}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.email}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Institution/Affiliation *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter your institution"
                      name="affiliation"
                      value={values.affiliation}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.affiliation && errors.affiliation}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.affiliation}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <h4 className="mt-5 mb-4">Article Information</h4>

                  <Form.Group className="mb-3">
                    <Form.Label>Article Title *</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder="Enter article title"
                      name="title"
                      value={values.title}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.title && errors.title}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.title}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Abstract *</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={5}
                      placeholder="Enter abstract (max 300 words)"
                      name="abstract"
                      value={values.abstract}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.abstract && errors.abstract}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.abstract}
                    </Form.Control.Feedback>
                    {values.abstract && (
                      <Form.Text className="text-muted">
                        Word count:{" "}
                        {
                          values.abstract
                            .split(" ")
                            .filter((word) => word.trim()).length
                        }
                        /300
                      </Form.Text>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Keywords *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter 3-5 keywords separated by commas"
                      name="keywords"
                      value={values.keywords}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.keywords && errors.keywords}
                    />
                    <Form.Text className="text-muted">
                      Separate keywords with commas (e.g., machine learning,
                      artificial intelligence, data analysis)
                    </Form.Text>
                    <Form.Control.Feedback type="invalid">
                      {errors.keywords}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label>Upload Manuscript *</Form.Label>
                    <Form.Control
                      type="file"
                      accept=".doc,.docx,.pdf"
                      onChange={handleFileChange}
                    />
                    <Form.Text className="text-muted">
                      Accepted file types: .doc, .docx, .pdf (Max size: 10MB)
                    </Form.Text>
                    {selectedFile && (
                      <Form.Text className="text-success">
                        Selected: {selectedFile.name} (
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                      </Form.Text>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Check
                      name="agreement"
                      checked={values.agreement}
                      onChange={handleChange}
                      isInvalid={touched.agreement && errors.agreement}
                      label="I confirm that this manuscript has not been published elsewhere and is not under consideration by another journal. *"
                      feedback={errors.agreement}
                      feedbackType="invalid"
                    />
                  </Form.Group>

                  <div className="d-flex gap-2 justify-content-end">
                    <Button
                      variant="outline-secondary"
                      onClick={() => window.history.back()}
                      disabled={isSubmitting}
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-paper-plane me-2"></i>
                          Submit Article
                        </>
                      )}
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </Container>
      </div>

      {/* Success Modal */}
      <SuccessModal
        show={showSuccessModal}
        onHide={closeSuccessModal}
        title="Article Submitted Successfully!"
        message="Thank you for submitting your research article to Asia Academic Press! We have received your submission and it is currently under review. Our editorial team will review your manuscript and get back to you within 2-3 weeks with feedback or a decision regarding publication."
      />
    </>
  );
};

export default SubmissionForm;
